import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import DesignCard from "../components/DesignCard";
import EmptyState from "../components/EmptyState";
import SignInPromptModal from "../components/SignInPromptModal";
import {
  formatInt,
  formatPriceFull,
  formatStatusLabel,
} from "../lib/format";
import {
  AlertTriangle,
  BadgeCheck,
  Hammer,
  Landmark,
  ChevronRight,
  Heart,
  ArrowUpRight,
  MessageSquare,
} from "lucide-react";
import BackBar from "../components/BackBar";

/* ───────── Reveal-on-scroll helper (local to this page) ───────── */

function useInView({ threshold = 0.15, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, as: As = "div", style, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <As
      ref={ref}
      className={`wn-reveal${inView ? " is-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </As>
  );
}

/* ───────── Constraint definitions ───────── */

const CONSTRAINT_DEFS = {
  demolition_required: {
    title: "Structure present — demolition required",
    tone: "terracotta",
    Icon: Hammer,
    body:
      "An existing structure on this parcel must be demolished before new construction can begin.",
  },
  historic_district: {
    title: "Historic district — IHPC certificate required",
    tone: "gold",
    Icon: Landmark,
    body:
      "Located in an IHPC historic district. A Certificate of Appropriateness from the Indianapolis Historic Preservation Commission is required.",
  },
  by_right_eligible: {
    title: "By-right construction eligible",
    tone: "forest",
    Icon: BadgeCheck,
    body:
      "Conforming projects may proceed without variance hearings.",
  },
  ten_day_path_eligible: {
    title: "10-day path eligible",
    tone: "forest",
    Icon: BadgeCheck,
    body:
      "Pre-reviewed designs qualifying on this parcel are eligible for an accelerated 10-day permitting review.",
  },
  easement: {
    title: "Easement on parcel",
    tone: "gold",
    Icon: AlertTriangle,
    body:
      "Recorded easement affects buildable area. Confirm setbacks during consultation.",
  },
  frontage_issue: {
    title: "Frontage issue",
    tone: "gold",
    Icon: AlertTriangle,
    body:
      "Lot frontage is non-standard. A variance may be required for some designs.",
  },
};

const TONE = {
  terracotta: { fg: "#8F3C23", bd: "rgba(143,60,35,0.45)" },
  gold:       { fg: "#8a651f", bd: "rgba(185,138,60,0.55)" },
  forest:     { fg: "#4F6149", bd: "rgba(79,97,73,0.50)" },
};

/* ───────── Page ───────── */

export default function ParcelDetail() {
  const { parcelId } = useParams();
  const navigate = useNavigate();
  const { role, user } = useRole();
  const [parcel, setParcel] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInAction, setSignInAction] = useState("");

  const isOwner = role === "owner_builder" && user?.id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/parcels/${parcelId}`),
      api.get(`/parcels/${parcelId}/compatible-designs`),
    ])
      .then(([p, c]) => {
        if (cancelled) return;
        setParcel(p.data);
        setMatches(c.data);
      })
      .catch((e) => !cancelled && setErr(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [parcelId]);

  const gated = (label) => () => {
    setSignInAction(label);
    setSignInOpen(true);
  };

  const handleSave = () => {
    if (!isOwner) return gated("Save this parcel")();
    // Saved-parcels backend wiring is deferred — surface a friendly inline state.
    setSignInAction("Save this parcel");
    setSignInOpen(true);
  };

  const handleFitStudy = () => {
    if (!isOwner) return gated("Request a fit study")();
    const q = new URLSearchParams({
      parcel: parcelId,
      intent: "fit-study",
    });
    navigate(`/owner/consultations/new?${q.toString()}`);
  };

  if (err) {
    return (
      <div data-testid="parcel-detail-error" style={{ color: "#A23B2E" }}>
        Could not load this parcel.{" "}
        <Link to="/parcels" style={{ color: "#1B1F4B" }}>
          Back to Parcels
        </Link>
      </div>
    );
  }
  if (loading || !parcel) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
        Loading parcel…
      </div>
    );
  }

  const constraints = parcel.constraints || [];
  const shortId = `P-${parcel.parcel_id
    .toString()
    .padStart(5, "0")
    .slice(-5)}`;
  const streetTitle = parcel.address_parts?.street
    ? toTitleCase(parcel.address_parts.street)
    : parcel.address;

  return (
    <section className="animate-fade-in-up" data-testid="parcel-detail-page">
      <BackBar
        parent={{ to: "/parcels", label: "Parcels" }}
        trail={[
          { label: "Parcels", to: "/parcels" },
          { label: streetTitle },
        ]}
      />

      {/* ─── § 01 · Editorial hero ─── */}
      <Reveal>
        <header
          data-testid="parcel-hero"
          className="grid lg:grid-cols-[1fr_auto] gap-10 items-end"
          style={{ paddingBottom: 28 }}
        >
          <div>
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 28,
                  height: 2,
                  background: "#B14E2F",
                }}
              />
              <span className="wn-label" style={{ color: "#8F3C23" }}>
                § 01 · Parcel survey
              </span>
            </div>

            <h1
              className="mt-5"
              data-testid="parcel-title"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: "clamp(40px, 5.4vw, 62px)",
                fontWeight: 500,
                lineHeight: 1.04,
                letterSpacing: "-0.01em",
                color: "#1B1F4B",
                margin: 0,
                maxWidth: "min(960px, 80vw)",
              }}
            >
              {streetTitle}
            </h1>

            <p
              className="mt-4"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 19,
                lineHeight: 1.55,
                maxWidth: 720,
                margin: 0,
              }}
            >
              A {formatInt(parcel.area_sqft)}-square-foot {parcel.zoning} lot
              in{" "}
              <span style={{ color: "#272636" }}>{parcel.neighborhood}</span>
              . Currently{" "}
              <span style={{ color: "#272636" }}>
                {formatStatusLabel(parcel.status).toLowerCase()}
              </span>{" "}
              — surveyed against the city&rsquo;s record for setbacks,
              easements, and historic overlays.
            </p>
          </div>

          {/* Right-rail dateline */}
          <aside
            data-testid="parcel-dateline"
            className="hidden lg:block"
            style={{
              borderLeft: "1px solid #D9D8E4",
              paddingLeft: 20,
              minWidth: 180,
            }}
          >
            <div className="wn-label" style={{ color: "#6C6B85" }}>
              Volume 01
            </div>
            <div
              className="mt-1 tabular-nums"
              style={{
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                color: "#1B1F4B",
                fontSize: 15,
                letterSpacing: "0.02em",
              }}
            >
              Parcel {shortId}
            </div>
            <div
              className="mt-3"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#6C6B85",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              Surveyed 2025 · Indianapolis,&nbsp;IN
            </div>
          </aside>
        </header>
      </Reveal>

      {/* ─── § 02 · Map + Unified fact sheet ─── */}
      <div
        className="grid lg:grid-cols-[1.05fr_1fr] gap-10 items-stretch"
        style={{ paddingTop: 24 }}
        data-testid="parcel-map-fact"
      >
        <Reveal style={{ display: "flex" }}>
          <div
            data-testid="parcel-mini-map"
            style={{
              width: "100%",
              height: "100%",
              minHeight: 520,
              border: "1px solid #D9D8E4",
              borderRadius: 4,
              overflow: "hidden",
              backgroundColor: "#EEEDF3",
            }}
          >
            <MapContainer
              center={[parcel.geo.lat, parcel.geo.lng]}
              zoom={17}
              scrollWheelZoom={false}
              dragging={false}
              doubleClickZoom={false}
              zoomControl={false}
              className="wn-leaflet-map"
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
                subdomains={["a", "b", "c", "d"]}
                maxZoom={19}
                attribution="&copy; OSM &copy; CARTO"
              />
              <Marker
                position={[parcel.geo.lat, parcel.geo.lng]}
                icon={L.divIcon({
                  className: "wn-parcel-marker",
                  iconSize: [22, 22],
                  iconAnchor: [11, 11],
                  html: `<span style="display:block;width:22px;height:22px;border:3px solid #B14E2F;background:rgba(177,78,47,0.18);border-radius:3px;"></span>`,
                })}
              />
            </MapContainer>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <FactSheet parcel={parcel} shortId={shortId} />
        </Reveal>
      </div>

      {/* ─── § 02b · "What the city knows" banners ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 02"
          eyebrow="What the city knows"
          title="Conditions of record."
          dek="Banners below reflect zoning, history, and known site conditions pulled from public registries."
        />
      </Reveal>
      <Reveal delay={100}>
        <div
          className="mt-8 grid md:grid-cols-2 lg:grid-cols-3"
          style={{
            borderTop: "1px solid #D9D8E4",
            borderBottom: "1px solid #D9D8E4",
          }}
          data-testid="constraint-banners"
        >
          {constraints.length === 0 ? (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: "26px 4px",
                color: "#6C6B85",
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                fontSize: 16,
              }}
            >
              No special constraints recorded for this parcel.
            </div>
          ) : (
            constraints.map((c, i) => {
              const def = CONSTRAINT_DEFS[c];
              if (!def) return null;
              const t = TONE[def.tone];
              const { Icon } = def;
              return (
                <div
                  key={c}
                  data-testid={`constraint-${c}`}
                  style={{
                    padding: "22px 22px 22px 0",
                    borderLeft:
                      i % 3 === 0 ? "none" : "1px solid #D9D8E4",
                    paddingLeft: i % 3 === 0 ? 0 : 22,
                    display: "grid",
                    gridTemplateColumns: "28px 1fr",
                    gap: 14,
                    alignItems: "start",
                  }}
                >
                  <Icon size={22} color={t.fg} strokeWidth={1.6} />
                  <div>
                    <div className="wn-label" style={{ color: t.fg }}>
                      {def.title}
                    </div>
                    <div
                      className="mt-2"
                      style={{
                        color: "#272636",
                        fontSize: 14,
                        lineHeight: 1.55,
                      }}
                    >
                      {def.body}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Reveal>

      {/* ─── § 03 · Match a design ─── */}
      <SectionDivider />
      <Reveal>
        <SectionHeading
          marker="§ 03"
          eyebrow="Match a design"
          title="Designs that fit this parcel."
          trailing={
            <div
              className="wn-label"
              style={{ color: "#6C6B85", textAlign: "right" }}
            >
              Matched on width&nbsp;≥&nbsp;{parcel.width_ft}′
              <span aria-hidden style={{ color: "#9AA0C0", margin: "0 8px" }}>
                ·
              </span>
              zoning&nbsp;{parcel.zoning}
            </div>
          }
        />
      </Reveal>

      {matches.length === 0 ? (
        <Reveal delay={80}>
          <div className="mt-8">
            <EmptyState
              title="No catalog designs match this parcel yet."
              body="Adjust your search, or request a custom consultation in a later step."
            />
          </div>
        </Reveal>
      ) : (
        <Reveal delay={80}>
          <div
            className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-[26px]"
            data-testid="compatible-grid"
          >
            {matches.map((d) => (
              <DesignCard
                key={d.id}
                design={d}
                contextLotLabel={`${parcel.width_ft} ft Lot Match`}
              />
            ))}
          </div>
        </Reveal>
      )}

      {/* ─── § 04 · Action band (inverted indigo) ─── */}
      <Reveal delay={60}>
        <ActionBand
          parcel={parcel}
          matchesCount={matches.length}
          onSave={handleSave}
          onFitStudy={handleFitStudy}
        />
      </Reveal>

      <SignInPromptModal
        open={signInOpen}
        onOpenChange={setSignInOpen}
        action={signInAction}
      />
    </section>
  );
}

/* ─────────────────────── Subcomponents ─────────────────────── */

function SectionDivider() {
  return (
    <div
      aria-hidden
      style={{
        borderTop: "1px solid #D9D8E4",
        marginTop: 64,
        marginBottom: 36,
      }}
    />
  );
}

function SectionHeading({ marker, eyebrow, title, dek, trailing }) {
  return (
    <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-end">
      <div>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 28,
              height: 2,
              background: "#B14E2F",
            }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            {marker} · {eyebrow}
          </span>
        </div>
        {title && (
          <h2
            className="mt-4"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(28px, 3.4vw, 38px)",
              lineHeight: 1.15,
              color: "#1B1F4B",
              letterSpacing: "-0.005em",
              margin: 0,
              paddingBottom: 4,
            }}
          >
            {title}
          </h2>
        )}
        {dek && (
          <p
            className="mt-3"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 16,
              lineHeight: 1.55,
              maxWidth: 680,
              margin: 0,
            }}
          >
            {dek}
          </p>
        )}
      </div>
      {trailing && <div>{trailing}</div>}
    </div>
  );
}

/* ─────────── Unified fact sheet (right column of § 02) ─────────── */

function FactSheet({ parcel, shortId }) {
  return (
    <div
      data-testid="parcel-fact-sheet"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        backgroundColor: "#FBFAFD",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* §  Specifications */}
      <div style={{ padding: "22px 24px 22px" }}>
        <SectionLabel>Specifications</SectionLabel>
        <div
          className="grid grid-cols-2 mt-3"
          style={{ columnGap: 24, rowGap: 16 }}
        >
          <Spec
            label="Lot dimensions"
            value={`${parcel.width_ft}′ × ${parcel.depth_ft}′`}
            numeric
          />
          <Spec
            label="Area"
            value={`${formatInt(parcel.area_sqft)} sqft`}
            numeric
          />
          <Spec label="Zoning" value={parcel.zoning} />
          <Spec
            label="Historic status"
            value={(parcel.historic_status || "—").replace(/_/g, " ")}
          />
          <Spec label="Neighborhood" value={parcel.neighborhood} />
          <Spec label="Status" value={formatStatusLabel(parcel.status)} />
        </div>
      </div>

      {/* §  Vacancy economics */}
      <div
        style={{
          padding: "20px 24px 22px",
          borderTop: "1px solid #D9D8E4",
        }}
      >
        <SectionLabel>Vacancy economics</SectionLabel>
        <div className="mt-3">
          <CostRow
            label="Assessed value"
            value={formatPriceFull(parcel.assessed_value)}
          />
          <CostRow
            label="Annual tax estimate"
            value={formatPriceFull(parcel.tax_estimate)}
          />
        </div>
        <div
          style={{
            borderTop: "1px solid #D9D8E4",
            marginTop: 12,
            paddingTop: 14,
          }}
          className="flex items-baseline justify-between"
        >
          <span className="wn-label" style={{ color: "#1B1F4B" }}>
            City revenue at vacancy
          </span>
          <span
            data-testid="parcel-vacant-total"
            className="tabular-nums"
            style={{
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 800,
              fontSize: 26,
              color: "#8F3C23",
              lineHeight: 1,
              letterSpacing: "-0.01em",
            }}
          >
            {formatPriceFull(parcel.tax_estimate)}
            <span
              className="wn-label"
              style={{ color: "#6C6B85", marginLeft: 8 }}
            >
              / yr
            </span>
          </span>
        </div>
      </div>

      {/* §  Provenance — flex:1 so the card matches map height */}
      <div
        style={{
          padding: "20px 24px 22px",
          borderTop: "1px solid #D9D8E4",
          flex: 1,
        }}
      >
        <SectionLabel>Provenance</SectionLabel>
        <ul
          className="mt-3"
          style={{ listStyle: "none", padding: 0, margin: 0 }}
        >
          <TimelineRow label="Parcel ID" value={shortId} first />
          <TimelineRow
            label="Address of record"
            value={toTitleCase(
              parcel.address_parts?.street || parcel.address,
            )}
          />
          <TimelineRow
            label="Sourced from"
            value="Marion County Assessor · Indy Maps"
          />
        </ul>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="wn-label" style={{ color: "#8F3C23" }}>
      {children}
    </div>
  );
}

function Spec({ label, value, numeric = false }) {
  return (
    <div>
      <div className="wn-label">{label}</div>
      <div
        className={numeric ? "tabular-nums" : ""}
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: numeric ? 700 : 600,
          color: "#1B1F4B",
          fontSize: numeric ? 22 : 16,
          marginTop: 4,
          lineHeight: 1.15,
          textTransform: !numeric && label === "Status" ? "none" : "none",
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function CostRow({ label, value }) {
  return (
    <div
      className="flex items-baseline justify-between"
      style={{ padding: "8px 0" }}
    >
      <span style={{ color: "#272636", fontSize: 14 }}>{label}</span>
      <span
        className="tabular-nums"
        style={{ fontWeight: 600, color: "#1B1F4B", fontSize: 15 }}
      >
        {value}
      </span>
    </div>
  );
}

function TimelineRow({ label, value, first }) {
  return (
    <li
      className="grid grid-cols-[1fr_auto] items-baseline"
      style={{
        borderTop: first ? "none" : "1px solid #EEEDF3",
        padding: "10px 0",
        columnGap: 16,
      }}
    >
      <span style={{ color: "#272636", fontSize: 14 }}>{label}</span>
      <span
        className="tabular-nums"
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 600,
          color: "#1B1F4B",
          fontSize: 13,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </li>
  );
}

/* ─────────── § 04 · Inverted indigo action band ─────────── */

function ActionBand({ parcel, matchesCount, onSave, onFitStudy }) {
  return (
    <div
      data-testid="parcel-action-band"
      style={{
        marginTop: 72,
        backgroundColor: "#1B1F4B",
        color: "#FBFAFD",
        borderRadius: 4,
        padding: "44px 44px 40px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Eyebrow */}
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#E8927C",
          }}
        />
        <span
          className="wn-label"
          style={{ color: "#E8927C" }}
        >
          § 04 · Take the next step
        </span>
      </div>

      <div
        className="mt-5 grid lg:grid-cols-[1fr_auto] items-end gap-8"
      >
        <div>
          <h3
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(28px, 3.2vw, 38px)",
              lineHeight: 1.12,
              letterSpacing: "-0.005em",
              color: "#FBFAFD",
              margin: 0,
              maxWidth: 720,
            }}
          >
            Find your fit on {toTitleCase(
              parcel.address_parts?.street || parcel.address,
            )}.
          </h3>
          <p
            className="mt-3"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "rgba(251,250,253,0.72)",
              fontSize: 17,
              lineHeight: 1.55,
              maxWidth: 620,
              margin: 0,
            }}
          >
            {matchesCount > 0
              ? `${matchesCount} pre-reviewed ${
                  matchesCount === 1 ? "design fits" : "designs fit"
                } this lot today. Save the parcel to revisit it, or request a fit study to confirm setbacks and start a 45-minute consultation.`
              : "No catalog designs fit this exact lot today. Request a fit study and we'll route the survey to an architect for a custom recommendation."}
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-3">
          <button
            type="button"
            onClick={onSave}
            data-testid="parcel-save-btn"
            className="wn-cta-arrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 18px",
              borderRadius: 4,
              background: "transparent",
              color: "#FBFAFD",
              border: "1px solid rgba(251,250,253,0.45)",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.01em",
              cursor: "pointer",
              transition:
                "background-color 200ms ease-out, border-color 200ms ease-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(251,250,253,0.08)";
              e.currentTarget.style.borderColor = "#FBFAFD";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor =
                "rgba(251,250,253,0.45)";
            }}
          >
            <Heart size={15} />
            Save parcel
          </button>

          <button
            type="button"
            onClick={onFitStudy}
            data-testid="parcel-fit-study-btn"
            className="wn-cta-arrow"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 20px",
              borderRadius: 4,
              background: "#B14E2F",
              color: "#FBFAFD",
              border: "1px solid #B14E2F",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.01em",
              cursor: "pointer",
              transition:
                "background-color 200ms ease-out, border-color 200ms ease-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#8F3C23";
              e.currentTarget.style.borderColor = "#8F3C23";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#B14E2F";
              e.currentTarget.style.borderColor = "#B14E2F";
            }}
          >
            <MessageSquare size={15} />
            Request fit study
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Utilities ─────────── */

function toTitleCase(s) {
  if (!s) return "";
  return s
    .toString()
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    // Re-capitalize compass directions and ordinals like "30Th" -> "30th"
    .replace(/(\d+)(St|Nd|Rd|Th)\b/gi, (_m, n, suf) => `${n}${suf.toLowerCase()}`);
}
